import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, UserCheck, UserX, Mail as MailIcon, Plus, Pencil, Trash2, Info, Filter, X, Clock, ArrowUp, ArrowDown } from 'lucide-react';
import {
  listContacts, createContact, updateContact, deleteContact,
  fetchContactSegmentMemberships, listSegments,
  type Contact, type ContactFilters, type ContactSortField, type Segment,
} from '../../lib/supabase';
import { contactMatchesSegment } from '../../lib/segmentEval';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { Modal } from '../../components/Modal/Modal';
import { HouseholdBadge } from '../../components/HouseholdBadge/HouseholdBadge';
import { formatNumber } from '../../lib/format';
import { useAuthStore } from '../../stores/authStore';

const PAGE_SIZE = 25;
const ORGANIZATIONS = [
  'Spandau', 'Spandau gGmbH', 'Charlottenburg', 'Steglitz', 'Tegel',
  'Waltersdorf', 'Mariendorf', 'Lübars', 'e.V.',
];

function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  const diff = Date.now() - date.getTime();
  const sec = Math.max(0, Math.floor(diff / 1000));
  if (sec < 60) return 'gerade';
  const min = Math.floor(sec / 60);
  if (min < 60) return `vor ${min} Min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `vor ${h} Std`;
  const d = Math.floor(h / 24);
  if (d < 7) return `vor ${d} Tg`;
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
}
function fullTimestamp(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

type FormState = {
  id?: string; crm_id?: string | null;
  email: string; first_name: string; last_name: string;
  organization: string; opt_in: boolean; tags: string;
};
const emptyForm: FormState = { email: '', first_name: '', last_name: '', organization: '', opt_in: true, tags: '' };

export function ContactList() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';

  const [filters, setFilters] = useState<ContactFilters>({
    search: '', organization: '', isMember: 'all', optIn: 'all', bounceStatus: 'all', tag: '',
  });
  const [sortBy, setSortBy] = useState<ContactSortField>('updated_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [error, setError] = useState('');

  const setFilter = <K extends keyof ContactFilters>(k: K, v: ContactFilters[K]) => {
    setFilters((f) => ({ ...f, [k]: v })); setPage(0);
  };
  const clearFilters = () => {
    setFilters({ search: '', organization: '', isMember: 'all', optIn: 'all', bounceStatus: 'all', tag: '' });
    setPage(0);
  };
  const toggleSort = (field: ContactSortField) => {
    if (sortBy === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(field); setSortDir('asc'); }
    setPage(0);
  };
  const activeFilterCount =
    (filters.search ? 1 : 0) + (filters.organization ? 1 : 0) +
    (filters.isMember && filters.isMember !== 'all' ? 1 : 0) +
    (filters.optIn && filters.optIn !== 'all' ? 1 : 0) +
    (filters.bounceStatus && filters.bounceStatus !== 'all' ? 1 : 0) +
    (filters.tag ? 1 : 0);

  const { data, isLoading } = useQuery({
    queryKey: ['contacts', filters, page, sortBy, sortDir],
    queryFn: () => listContacts(filters, PAGE_SIZE, page * PAGE_SIZE, sortBy, sortDir),
  });

  const staticMembershipsQuery = useQuery({
    queryKey: ['contact-memberships'],
    queryFn: fetchContactSegmentMemberships,
  });
  const staticMembershipsByContact = useMemo(() => {
    const r: Record<string, { id: string; name: string }[]> = {};
    for (const m of staticMembershipsQuery.data ?? []) (r[m.contact_id] ||= []).push({ id: m.segment_id, name: m.segment_name });
    return r;
  }, [staticMembershipsQuery.data]);

  const allSegmentsQuery = useQuery<Segment[], Error>({
    queryKey: ['all-segments-with-filters'],
    queryFn: listSegments,
  });
  const dynamicSegments = useMemo(
    () => (allSegmentsQuery.data ?? []).filter((s) => s.is_dynamic),
    [allSegmentsQuery.data],
  );
  const dynamicGroupsForContact = (c: Contact) =>
    dynamicSegments.filter((s) => contactMatchesSegment(c, s)).map((s) => ({ id: s.id, name: s.name }));

  const saveMutation = useMutation({
    mutationFn: async (f: FormState) => {
      const payload: Partial<Contact> = {
        email: f.email.trim().toLowerCase(),
        first_name: f.first_name || null, last_name: f.last_name || null,
        organization: f.organization || null, school: f.organization || null,
        opt_in: f.opt_in,
        tags: f.tags ? f.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
      };
      if (f.id) return updateContact(f.id, payload);
      return createContact(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      setModalOpen(false); setForm(emptyForm); setError('');
    },
    onError: (e) => setError((e as Error).message),
  });
  const deleteMutation = useMutation({
    mutationFn: deleteContact,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['contacts'] }),
  });

  const openNew = () => { setForm(emptyForm); setError(''); setModalOpen(true); };
  const openEdit = (c: Contact) => {
    setForm({
      id: c.id, crm_id: c.crm_id, email: c.email,
      first_name: c.first_name ?? '', last_name: c.last_name ?? '',
      organization: c.organization ?? c.school ?? '',
      opt_in: c.opt_in, tags: (c.tags ?? []).join(', '),
    });
    setError(''); setModalOpen(true);
  };
  const handleDelete = (c: Contact) => {
    if (!confirm(`Kontakt "${c.email}" wirklich löschen?`)) return;
    deleteMutation.mutate(c.id);
  };

  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));

  const SortIcon = ({ field }: { field: ContactSortField }) => {
    if (sortBy !== field) return null;
    return sortDir === 'asc' ? <ArrowUp size={12} style={{ marginLeft: 4, verticalAlign: '-1px' }} /> : <ArrowDown size={12} style={{ marginLeft: 4, verticalAlign: '-1px' }} />;
  };
  const sortableHeaderStyle: React.CSSProperties = { cursor: 'pointer', userSelect: 'none' };

  return (
    <div>
      <PageHeader
        title="Kontakte"
        subtitle={isAdmin
          ? 'Kontakte aus CRM (read-only) + manuell angelegte. Filterbar nach Studio, Mitglied/Lead, Opt-In, Bounce, Tag.'
          : 'Gespiegelte CRM-Kontakte (read-only)'}
        actions={isAdmin && (
          <button className="btn btn-primary" onClick={openNew}><Plus size={16} /> Neuer Kontakt</button>
        )}
      />

      <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
        <Info size={16} />
        <div>
          <strong>Versendet wird ausschließlich an:</strong> echte Vertragsmitglieder (Mitglied im CRM)
          mit aktivem Opt-In, ohne Abmeldung und ohne Hard-Bounce.
        </div>
      </div>

      <div className="kpi-grid">
        <div className="kpi-card">
          <p className="kpi-label">E-Mails (Postfächer)</p>
          <div className="kpi-value">{formatNumber(data?.totalEmails ?? 0)}</div>
          <p className="kpi-hint">
            {activeFilterCount > 0
              ? <>davon im Filter: <strong>{formatNumber(data?.filteredTotal ?? 0)}</strong></>
              : 'jede Zeile = 1 Inbox'}
          </p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">Personen (CRM)</p>
          <div className="kpi-value" style={{ color: 'var(--success-dark)' }}>{formatNumber(data?.totalPersons ?? 0)}</div>
          <p className="kpi-hint">
            davon Mitglieder: <strong>{formatNumber(data?.membersPersons ?? 0)}</strong> (in {formatNumber(data?.membersEmails ?? 0)} Postfächern)
          </p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">Versendbar (Opt-In)</p>
          <div className="kpi-value" style={{ color: 'var(--success-dark)' }}>{formatNumber(data?.optInEmails ?? 0)}</div>
          <p className="kpi-hint">opt_in + nicht abgemeldet</p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">Nicht versendbar</p>
          <div className="kpi-value" style={{ color: 'var(--danger-dark)' }}>{formatNumber((data?.totalEmails ?? 0) - (data?.optInEmails ?? 0))}</div>
          <p className="kpi-hint">kein Opt-In oder abgemeldet</p>
        </div>
      </div>

      <div className="card" style={{ padding: '0.75rem 1rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
            <Filter size={14} /> Filter:
          </div>
          <div className="search-input-wrapper" style={{ flex: 1, minWidth: 220 }}>
            <Search size={16} />
            <input className="search-input" placeholder="E-Mail / Vor- / Nachname suchen..."
              value={filters.search ?? ''} onChange={(e) => setFilter('search', e.target.value)} />
          </div>
          <select className="form-select" style={{ maxWidth: 180 }}
            value={filters.organization ?? ''} onChange={(e) => setFilter('organization', e.target.value)}>
            <option value="">Alle Studios</option>
            {ORGANIZATIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="form-select" style={{ maxWidth: 180 }}
            value={filters.isMember ?? 'all'} onChange={(e) => setFilter('isMember', e.target.value as ContactFilters['isMember'])}>
            <option value="all">Mitglieder + Leads</option>
            <option value="yes">Nur Mitglieder</option>
            <option value="no">Nur Nicht-Mitglieder</option>
          </select>
          <select className="form-select" style={{ maxWidth: 200 }}
            value={filters.optIn ?? 'all'} onChange={(e) => setFilter('optIn', e.target.value as ContactFilters['optIn'])}>
            <option value="all">Alle Opt-In-Stati</option>
            <option value="yes">Mit Opt-In (versendbar)</option>
            <option value="no">Ohne Opt-In</option>
            <option value="unsubscribed">Abgemeldet</option>
          </select>
          <select className="form-select" style={{ maxWidth: 170 }}
            value={filters.bounceStatus ?? 'all'} onChange={(e) => setFilter('bounceStatus', e.target.value as ContactFilters['bounceStatus'])}>
            <option value="all">Alle Bounce-Stati</option>
            <option value="none">Ohne Bounce</option>
            <option value="soft">Soft Bounce</option>
            <option value="hard">Hard Bounce</option>
          </select>
          <input className="form-input" style={{ maxWidth: 180 }} placeholder="Tag (z.B. trainer)"
            value={filters.tag ?? ''} onChange={(e) => setFilter('tag', e.target.value)} />
          {activeFilterCount > 0 && (
            <button className="btn btn-ghost btn-sm" onClick={clearFilters} title="Alle Filter zurücksetzen">
              <X size={14} /> {activeFilterCount} Filter
            </button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><div className="loading-spinner" /></div>
      ) : !data || data.contacts.length === 0 ? (
        <EmptyState
          icon={MailIcon}
          title={activeFilterCount > 0 ? 'Keine Treffer' : 'Noch keine Kontakte'}
          description={activeFilterCount > 0 ? 'Andere Filter probieren oder zurücksetzen.' : 'Manuell anlegen oder auf den nächsten CRM-Sync warten.'}
          action={isAdmin && activeFilterCount === 0 ? (
            <button className="btn btn-primary" onClick={openNew}><Plus size={16} /> Ersten Kontakt anlegen</button>
          ) : undefined}
        />
      ) : (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <th onClick={() => toggleSort('email')}        style={sortableHeaderStyle}>E-Mail<SortIcon field="email" /></th>
                <th onClick={() => toggleSort('last_name')}    style={sortableHeaderStyle}>Name<SortIcon field="last_name" /></th>
                <th onClick={() => toggleSort('organization')} style={sortableHeaderStyle}>Studio<SortIcon field="organization" /></th>
                <th onClick={() => toggleSort('is_member')}    style={sortableHeaderStyle}>Status<SortIcon field="is_member" /></th>
                <th onClick={() => toggleSort('opt_in')}       style={sortableHeaderStyle}>Opt-In<SortIcon field="opt_in" /></th>
                <th onClick={() => toggleSort('updated_at')}   style={sortableHeaderStyle}><Clock size={12} style={{ verticalAlign: '-2px' }} /> Aktualisiert<SortIcon field="updated_at" /></th>
                <th>Gruppen</th>
                {isAdmin && <th style={{ width: 120, textAlign: 'right' }}>Aktionen</th>}
              </tr>
            </thead>
            <tbody>
              {data.contacts.map((c) => {
                const staticG = staticMembershipsByContact[c.id] ?? [];
                const dynamicG = dynamicGroupsForContact(c);
                const allG = [...dynamicG, ...staticG]; // dynamische zuerst (typisch wichtiger)
                const bounceLabel = c.bounce_status === 'hard'
                  ? <span className="status-badge" style={{ background: 'var(--danger-light)', color: 'var(--danger-dark)' }}>Hard Bounce</span>
                  : c.bounce_status === 'soft'
                  ? <span className="status-badge" style={{ background: 'var(--warning-light, #fff7e6)', color: 'var(--warning-dark, #b25e00)' }}>Soft Bounce</span>
                  : null;
                return (
                  <tr key={c.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>{c.email}</td>
                    <td>
                      {[c.first_name, c.last_name].filter(Boolean).join(' ') || <em style={{ color: 'var(--text-tertiary)' }}>—</em>}
                      {c.household_member_count > 1 && (
                        <HouseholdBadge
                          email={c.email}
                          memberCount={c.household_member_count}
                          memberNames={c.household_member_names}
                          primaryCrmId={c.crm_id} />
                      )}
                    </td>
                    <td>
                      {c.household_organizations.length > 1
                        ? c.household_organizations.join(', ')
                        : (c.organization ?? c.school ?? <em style={{ color: 'var(--text-tertiary)' }}>—</em>)}
                    </td>
                    <td>
                      {c.is_member ? (
                        <span className="status-badge" style={{ background: 'var(--success-light)', color: 'var(--success-dark)' }}>Mitglied</span>
                      ) : c.crm_id ? (
                        <span className="status-badge" style={{ background: 'var(--surface-secondary)', color: 'var(--text-secondary)' }} title={c.customer_status ?? ''}>{c.customer_status ?? 'Lead'}</span>
                      ) : (
                        <span className="status-badge" style={{ background: 'var(--info-light)', color: 'var(--info-dark)' }}>manuell</span>
                      )}
                      {bounceLabel}
                    </td>
                    <td>
                      {c.unsubscribed_at ? (
                        <span className="status-badge" style={{ background: 'var(--danger-light)', color: 'var(--danger-dark)' }}><UserX size={11} /> abgemeldet</span>
                      ) : c.opt_in ? (
                        <span className="status-badge" style={{ background: 'var(--success-light)', color: 'var(--success-dark)' }}><UserCheck size={11} /> versendbar</span>
                      ) : (
                        <span className="status-badge" style={{ background: 'var(--surface-secondary)', color: 'var(--text-secondary)' }}>kein Opt-In</span>
                      )}
                    </td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}
                      title={`Geaendert: ${fullTimestamp(c.updated_at)}\nLetzter Sync: ${fullTimestamp(c.last_synced_at)}`}>
                      {timeAgo(c.updated_at)}
                    </td>
                    <td>
                      {allG.length === 0 ? (
                        <em style={{ color: 'var(--text-tertiary)', fontSize: '0.8125rem' }}>keine</em>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                          {allG.map((g) => (
                            <a key={g.id} href={`/segments/${g.id}`} className="status-badge"
                              style={{ background: 'var(--info-light)', color: 'var(--info-dark)', textDecoration: 'none', fontWeight: 500 }}
                              title={`Gruppe ${g.name} öffnen`} onClick={(e) => e.stopPropagation()}>
                              {g.name}
                            </a>
                          ))}
                        </div>
                      )}
                    </td>
                    {isAdmin && (
                      <td style={{ textAlign: 'right' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => openEdit(c)} title="Bearbeiten"><Pencil size={13} /></button>
                        <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => handleDelete(c)} title="Löschen"><Trash2 size={13} /></button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1.5rem' }}>
              <button className="btn btn-secondary btn-sm" disabled={page === 0} onClick={() => setPage(page - 1)}>Zurück</button>
              <span style={{ display: 'flex', alignItems: 'center', padding: '0 1rem', fontSize: '0.875rem' }}>Seite {page + 1} von {totalPages}</span>
              <button className="btn btn-secondary btn-sm" disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)}>Weiter</button>
            </div>
          )}
        </>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={form.id ? 'Kontakt bearbeiten' : 'Neuer Kontakt'}>
        {error && <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{error}</div>}
        {form.crm_id && (
          <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
            <Info size={16} />
            <div>CRM-Kontakt — Name/Studio werden vom nächsten Sync überschrieben. Tags + Opt-In sind editierbar.</div>
          </div>
        )}
        <div className="form-group">
          <label className="form-label">E-Mail *</label>
          <input className="form-input" type="email" placeholder="name@beispiel.de"
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!form.id} />
          {form.id && <p className="form-hint">E-Mail kann nicht geändert werden.</p>}
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Vorname</label>
            <input className="form-input" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} disabled={!!form.crm_id} />
          </div>
          <div className="form-group">
            <label className="form-label">Nachname</label>
            <input className="form-input" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} disabled={!!form.crm_id} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Studio</label>
          <select className="form-select" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} disabled={!!form.crm_id}>
            <option value="">— keines —</option>
            {ORGANIZATIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Tags (kommagetrennt)</label>
          <input className="form-input" placeholder="z.B. trainer, fight-night" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
        </div>
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" checked={form.opt_in} onChange={(e) => setForm({ ...form, opt_in: e.target.checked })} style={{ accentColor: 'var(--primary)', width: 18, height: 18 }} />
            <span><strong>Marketing-Opt-In</strong> aktiv</span>
          </label>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
          <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>Abbrechen</button>
          <button className="btn btn-primary" onClick={() => saveMutation.mutate(form)} disabled={!form.email.trim() || saveMutation.isPending}>
            {saveMutation.isPending ? 'Speichere...' : (form.id ? 'Speichern' : 'Anlegen')}
          </button>
        </div>
      </Modal>
    </div>
  );
}
